import {useEffect, useMemo} from 'react';
import useGetUserGamesApi from '@/Hooks/GetUserGamesApi/useGetUserGamesApi';

export interface TimeControlOption {
  control: string;
  label: string;
  bucket: 'Bullet' | 'Blitz' | 'Rapid' | 'Classical';
}

// snapToTimeControl (below) matches a played game's time_control to whichever
// of these chips has the closest BASE time, increment only as a tie-break —
// see parseTimeControl / snapToTimeControl for the contract.
export const TIME_CONTROLS: TimeControlOption[] = [
  {control: '2+0', label: '2 + 0', bucket: 'Bullet'},
  {control: '3+2', label: '3 + 2', bucket: 'Blitz'},
  {control: '5+3', label: '5 + 3', bucket: 'Blitz'},
  {control: '10+5', label: '10 + 5', bucket: 'Rapid'},
  {control: '15+10', label: '15 + 10', bucket: 'Rapid'},
  {control: '25+10', label: '25 + 10', bucket: 'Classical'},
];

export const DEFAULT_TIME_CONTROL = '5+3';

// CC saves store seconds ('300 + 0', '600 + 5'), imports store bare seconds
// ('600'), legacy rows are ''. Older data may be minutes-based ('3+2') — a
// base under 30 can only be minutes (nobody plays a sub-30-second base).
export function parseTimeControl(
  raw?: string | null,
): {baseSec: number; incSec: number} | null {
  if (!raw) return null;
  const [basePart, incPart] = String(raw).split('+');
  const base = Number(String(basePart).trim());
  if (!Number.isFinite(base) || base <= 0) return null;
  const inc = Number(String(incPart ?? '0').trim());
  return {
    baseSec: base < 30 ? base * 60 : base,
    incSec: Number.isFinite(inc) ? inc : 0,
  };
}

// TIME_CONTROLS' own `control` strings ('3+2', '25+10', ...) are minutes-based,
// so they parse through the same base<30 rule as any legacy minutes-based row.
const CHIPS_PARSED = TIME_CONTROLS.map(o => ({
  control: o.control,
  ...(parseTimeControl(o.control) as {baseSec: number; incSec: number}),
}));

export function snapToTimeControl(raw?: string | null): string {
  const parsed = parseTimeControl(raw);
  if (!parsed) return DEFAULT_TIME_CONTROL;

  return CHIPS_PARSED.reduce((best, o) => {
    const baseDiff = Math.abs(o.baseSec - parsed.baseSec);
    const bestBaseDiff = Math.abs(best.baseSec - parsed.baseSec);
    if (baseDiff !== bestBaseDiff) return baseDiff < bestBaseDiff ? o : best;
    // Tie-break: closest increment.
    const incDiff = Math.abs(o.incSec - parsed.incSec);
    const bestIncDiff = Math.abs(best.incSec - parsed.incSec);
    return incDiff < bestIncDiff ? o : best;
  }).control;
}

export function useRecommendedTimeControl(): string {
  const {mutate: fetchGames, data} = useGetUserGamesApi();

  useEffect(() => {
    fetchGames({page: 1});
  }, [fetchGames]);

  return useMemo(() => {
    const games: any[] = (data as any)?.data?.data ?? [];
    // Legacy rows can carry an empty time_control -- skip past those to the
    // first game that actually parses, instead of blindly taking games[0].
    const lastPlayed = games.find(g =>
      parseTimeControl(g?.time_control),
    )?.time_control;
    return snapToTimeControl(lastPlayed);
  }, [data]);
}
